import React from "react";
import {
  ContractPriceTemplateFilter,
  ContractsPriceRecord,
  ContractsPriceTemplates,
  HighHolidayOption,
  PriceListDay
} from "@graphql";
import { Event, MoreVert } from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import { sortBy, unionBy } from "lodash";
import { ContractsPriceTemplateType, PriceListWorkType } from "@bonliva-crm/shared";

export type Template = ContractsPriceTemplates.Return["list"]["items"][0];

export type Filter = ContractPriceTemplateFilter;

export type Record = ContractsPriceRecord;

export type ContractVariant = "template" | "customerContract" | "consultantContract";

export const initialSearchParams = {
  text: ""
  // type: null
};

// @params
// holidayOptions (aka highHolidayOption) - get from highHoliday repo
// items - highHolidays from some entity for exp CustomerContract, ConsultantContract, ContractsPriceTemplate
export const mergeHighHolidays = (holidayOptions: HighHolidayOption[], items) => {
  const highHolidaysOptions = holidayOptions?.map((x) => {
    const itemHH = items?.find((h) => h.option.name === x.name);
    if (itemHH) {
      return {
        _id: itemHH._id,
        option: itemHH.option,
        isGreatHoliday: itemHH.isGreatHoliday
      };
    } else {
      return {
        _id: null,
        option: x,
        isGreatHoliday: null
      };
    }
  });
  // avoid removed holidays
  const uniqList = unionBy(highHolidaysOptions, items ?? [], (x) => x.option.name);
  return sortBy(uniqList, ["option.listPosition"]);
};

const defaultDays = [
  PriceListDay.BusinessDay,
  PriceListDay.Weekends,
  PriceListDay.SqueezedDay,
  PriceListDay.DayAfterHoliday,
  PriceListDay.NearestWeekdayAfterHoliday,
  PriceListDay.DayBeforeHoliday,
  PriceListDay.NearestWeekdayBeforeHoliday,
  PriceListDay.DayAfterMajorHoliday,
  PriceListDay.NearestWeekdayAfterGreatHoliday,
  PriceListDay.DayBeforeMajorHoliday,
  PriceListDay.Holiday,
  PriceListDay.MajorHoliday,
  PriceListDay.Mon,
  PriceListDay.Tue,
  PriceListDay.Wed,
  PriceListDay.Thu,
  PriceListDay.Fri,
  PriceListDay.Sat,
  PriceListDay.Sun,
  PriceListDay.AnyTime
].map((v) => ({ label: locale.contractsPrice.priceListDay[v], value: v }));

export const dailyRecordOpt = [
  PriceListDay.Mon,
  PriceListDay.Tue,
  PriceListDay.Wed,
  PriceListDay.Thu,
  PriceListDay.Fri,
  PriceListDay.Sat,
  PriceListDay.Sun
].map((v) => ({ label: locale.contractsPrice.priceListDay[v], value: v }));

const startDays = [
  PriceListDay.DayBeforeNewYearsEve,
  PriceListDay.NewYearsEve,
  PriceListDay.NewYearsDay,
  PriceListDay.WeekdayBeforeTwelfthDay,
  PriceListDay.TwelfthDay,
  PriceListDay.DayBeforeEasterFriday,
  PriceListDay.EasterFriday,
  PriceListDay.EasterSaturday,
  PriceListDay.EasterSunday,
  PriceListDay.EasterMonday,
  PriceListDay.WeekdayBeforeFirstOfMay,
  PriceListDay.FirstOfMay,
  PriceListDay.WeekdayBeforeAscensionDay,
  PriceListDay.AscensionDay,
  PriceListDay.DayBeforeWhitesunDay,
  PriceListDay.WhitsunEve,
  PriceListDay.WhitSunday,
  PriceListDay.WhitSunday,
  PriceListDay.WhitMonday,
  PriceListDay.WeekdayBeforeSwedensNationalDay,
  PriceListDay.SwedensNationalDay,
  PriceListDay.DayBeforeMidsummerEve,
  PriceListDay.MidsummerEve,
  PriceListDay.MidsummerDay,
  PriceListDay.WeekdayBeforeAllSaintsDay,
  PriceListDay.AllSaintsDay,
  PriceListDay.DayBeforeChristmasEve,
  PriceListDay.ChristmasEve,
  PriceListDay.ChristmasDay,
  PriceListDay.BoxingDay
].map((v) => ({
  label: locale.contractsPrice.priceListDay[v],
  value: v,
  parent: "holidays"
}));

const endDays = [
  PriceListDay.WeekdayAfterNewYearsDay,
  PriceListDay.WeekdayAfterTwelfthDay,
  PriceListDay.DayAfterEasterMonday,
  PriceListDay.WeekdayAfterFirstOfMay,
  PriceListDay.WeekdayAfterAscensionDay,
  PriceListDay.DayAfterWhitsunMonday,
  PriceListDay.WeekdayAfterSwedensNationalDay,
  PriceListDay.WeekdayAfterMidsummerDay,
  PriceListDay.WeekdayAfterBoxingDay
].map((v) => ({
  label: locale.contractsPrice.priceListDay[v],
  value: v,
  parent: "holidays"
}));

const groupOption = {
  value: "holidays",
  label: locale.SpecificHighHoliday,
  unselectable: true,
  skipCheckbox: true,
  icon: <Event style={{ marginRight: 10 }} />
};

export const startDayOpt = [...defaultDays, groupOption, ...startDays];

export const startDayOptFullPeriod = startDayOpt.filter((x) => {
  return (
    x.value !== PriceListDay.BusinessDay &&
    x.value !== PriceListDay.Weekends &&
    x.value !== PriceListDay.SqueezedDay
  );
});

export const endDayOpt = [...defaultDays, groupOption, ...endDays];

export const endDayOptFullPeriod = endDayOpt.filter((x) => {
  return (
    x.value !== PriceListDay.BusinessDay &&
    x.value !== PriceListDay.Weekends &&
    x.value !== PriceListDay.SqueezedDay
  );
});

export const defaultWorkTypes = [
  PriceListWorkType.Active,
  PriceListWorkType.OnCall,
  PriceListWorkType.OnCallJour,
  PriceListWorkType.OnCallBer,
  PriceListWorkType.OnCallA,
  PriceListWorkType.OnCallB,
  PriceListWorkType.Normal,
  PriceListWorkType.OnCallPassive,
  PriceListWorkType.Overtime
].map((v) => ({ label: locale.contractsPrice.priceListWorkType[v], value: v }));

export const getDoctorWorkTypes = (isConsultantContract: boolean) => {
  const types = [
    PriceListWorkType.NeuropsychiatricExaminationChildrenAndYoungPeople,
    PriceListWorkType.NeuropsychologicalExaminationAdults,
    PriceListWorkType.MedicalExaminationAdults,
    PriceListWorkType.MedicalExaminationChildrenAndYoungPeople
  ];

  if (isConsultantContract) {
    types.push(
      PriceListWorkType.PrivateNPAssessmentAdults,
      PriceListWorkType.PrivateNPAssessmentChildrenAndYoung
    );
  }

  return types.map((v) => ({
    label: locale.contractsPrice.priceListWorkType[v],
    value: v
  }));
};

export const getPsychologistWorkTypes = (isConsultantContract: boolean) => {
  const types = [
    PriceListWorkType.ChildAndYouthInvestigation,
    PriceListWorkType.InvestigationWithinSchool,
    PriceListWorkType.AdultInvestigation,
    PriceListWorkType.BasicInvestigation,
    PriceListWorkType.AutismInvestigation,
    PriceListWorkType.ChildAndAdolescentInvestigationADHDADD,
    PriceListWorkType.ChildAndAdolescentInquiryAutism,
    PriceListWorkType.AdultEducationADHDADD,
    PriceListWorkType.AdultStudiesAutism,
    PriceListWorkType.NeuropsychiatricExaminationChildrenAndYoungPeople,
    PriceListWorkType.NeuropsychiatricExaminationChildrenAndYoungPeopleStep3,
    PriceListWorkType.NeuropsychologicalExaminationAdults,
    PriceListWorkType.NeuropsychologicalExaminationAdultsStep3,
    PriceListWorkType.PsychologicalInvestigationAdults,
    PriceListWorkType.PsychologicalInvestigationChildrenAndYoungPeople
  ];

  if (isConsultantContract) {
    types.push(
      PriceListWorkType.PrivateAssessmentCall,
      PriceListWorkType.PrivateNPAssessmentChildrenAndYoung,
      PriceListWorkType.PrivateNPAssessmentAdults
    );
  }

  return types
    .map((v) => ({
      label: locale.contractsPrice.priceListWorkType[v],
      value: v,
      parent: "Psychologist"
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const groupOptionWorkType = {
  value: "Psychologist",
  label: locale.Psychologist,
  unselectable: true,
  skipCheckbox: true,
  icon: <MoreVert style={{ marginRight: 10 }} />
};

export const getWorkTypeOpt = (isConsultantContract: boolean) => [
  ...defaultWorkTypes,
  groupOptionWorkType,
  ...getPsychologistWorkTypes(isConsultantContract)
];

export function getIsConultantContract({
  type,
  variant
}: {
  type?: ContractsPriceTemplateType;
  variant?: ContractVariant;
}) {
  return (
    type === ContractsPriceTemplateType.ConsultantContract ||
    variant === "consultantContract"
  );
}

export const hideBC_doctorWorkTypes = [
  PriceListWorkType.Active,
  PriceListWorkType.OnCall,
  PriceListWorkType.OnCallJour,
  PriceListWorkType.OnCallBer,
  PriceListWorkType.OnCallA,
  PriceListWorkType.OnCallB,
  PriceListWorkType.Normal,
  PriceListWorkType.OnCallPassive,
  PriceListWorkType.Overtime
];
export const hideBC_psyWorkTypes = [
  PriceListWorkType.Active,
  PriceListWorkType.OnCall,
  PriceListWorkType.OnCallJour,
  PriceListWorkType.OnCallBer,
  PriceListWorkType.OnCallA,
  PriceListWorkType.OnCallB,
  PriceListWorkType.Normal,
  PriceListWorkType.OnCallPassive,
  PriceListWorkType.Overtime,
  PriceListWorkType.ChildAndYouthInvestigation,
  PriceListWorkType.AutismInvestigation,
  PriceListWorkType.ChildAndAdolescentInvestigationADHDADD,
  PriceListWorkType.ChildAndAdolescentInquiryAutism,
  PriceListWorkType.AdultEducationADHDADD,
  PriceListWorkType.AdultStudiesAutism,
  PriceListWorkType.BasicInvestigation,
  PriceListWorkType.AdultInvestigation,
  PriceListWorkType.InvestigationWithinSchool
];
